import { GetCorporateProjectsResponse } from '@klokgroep/sanity';
import { NextRouter } from 'next/router';

export function getFilterPropertiesForKey(
  corporateProjects: GetCorporateProjectsResponse[],
  router: NextRouter,
  key: keyof NonNullable<GetCorporateProjectsResponse['properties']>,
  title: string
) {
  const selected = router.query[key] ? router.query[key]!.toString().split(',') : [];
  const options = getPropertyOptions(corporateProjects, key, selected);

  const onChange = (value: string) => {
    const newValue = selected.includes(value) ? selected.filter((type) => type !== value) : [...selected, value];
    const query = { ...router.query, page: 1, [key]: newValue };
    router.replace({ query }, { pathname: router.asPath.split('?')[0], query }, { shallow: true });
  };

  return { key, options, selected, onChange, title };
}

export function getFilteredProjects(
  corporateProjects: GetCorporateProjectsResponse[],
  keyValuePairs: Record<keyof NonNullable<GetCorporateProjectsResponse['properties']>, string[] | undefined>,
  query?: string
) {
  let newCorporateProjects = [...corporateProjects];

  for (const [key, value] of Object.entries(keyValuePairs)) {
    if ((value || []).length > 0) {
      newCorporateProjects = newCorporateProjects.filter((project) => {
        const property = project.properties
          ? project.properties[key as keyof NonNullable<GetCorporateProjectsResponse['properties']>]
          : undefined;
        return property?.some((property_) => property_?.slug?.current && value?.includes(property_?.slug?.current));
      });
    }
  }

  if (query) {
    const lowerCasedQuery = query.toLowerCase().trim();
    newCorporateProjects = newCorporateProjects.filter(
      (project) =>
        project.title?.toLowerCase().trim().includes(lowerCasedQuery) ||
        project.location?.toLowerCase().trim().includes(lowerCasedQuery)
    );
  }

  return newCorporateProjects;
}

function getPropertyOptions(
  corporateProjects: GetCorporateProjectsResponse[],
  type: keyof NonNullable<GetCorporateProjectsResponse['properties']>,
  selected?: string[]
) {
  const allOptions = corporateProjects
    .flatMap(({ properties }) => {
      if (!properties) {
        return;
      }

      const optionsForType = properties[type];

      if (!optionsForType) {
        return;
      }

      return optionsForType.filter(Boolean).flatMap(({ label, slug }) => ({
        checked: selected?.includes(slug?.current || ''),
        id: slug?.current || '',
        label: label,
      }));
    })
    .filter(
      (option): option is { checked: boolean; id: string; label: string } => !!option && !!option.label && !!option.id
    );

  const uniqueOptions = [...new Map(allOptions.map((item) => [item['id'], item])).values()];
  uniqueOptions.sort((a, b) => (a.label < b.label ? -1 : 1));
  return uniqueOptions;
}
